import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { soilProgramService } from '../../services/soilProgramService';

const SoilProgramImportPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [season, setSeason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    { label: t('nutrition.soil.workspace.title'), to: '/nutrition/soil' },
    { label: t('nutrition.soil.workspace.tab.setup'), to: '/nutrition/soil/setup' },
    { label: t('nutrition.soil.crumb.import') },
  ]);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null);
  };

  const readFileAsText = (f: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(f);
  });

  const onImport = async () => {
    if (!file) { setError(t('nutrition.soil.import.noFile')); return; }
    setBusy(true); setError('');
    try {
      const csv = await readFileAsText(file);
      const res = await soilProgramService.importProgram(csv, season || undefined, { filename: file.name });
      navigate(`/nutrition/soil/programs/${res.program._id}/review`);
    } catch (err) {
      setError(t('nutrition.soil.import.failed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="pat-page">
      <PageHeader title={t('nutrition.soil.import.title')} />
      <div className="pat-card">
        <label htmlFor="soil-csv">{t('nutrition.soil.import.fileLabel')}</label>
        <input id="soil-csv" data-testid="soil-csv-input" type="file" accept=".csv,text/csv" onChange={onFile} />
        <label htmlFor="soil-season">{t('nutrition.soil.import.seasonLabel')}</label>
        <input id="soil-season" value={season} onChange={(e) => setSeason(e.target.value)} placeholder="2025-2026" />
        {error && <div className="ntr-error" role="alert">{error}</div>}
        <button className="pat-btn pat-btn--primary" disabled={busy} onClick={onImport}>
          {t('nutrition.soil.import.submit')}
        </button>
      </div>
    </div>
  );
};

export default SoilProgramImportPage;
